import { Device } from '@twilio/voice-sdk'
import { toast } from 'react-toastify'
import {
  createPortalVoiceBootstrap,
  renewPortalVoiceCall,
} from 'src/mentor/services/portalVoiceCallService'

const PROVIDER_TWILIO = 'twilio'
const PROVIDER_PLIVO = 'plivo'
const PLIVO_TRANSPORT_TOKEN_HEADER = 'X-PH-Transporttoken'
const listeners = new Set()

let plivoConstructorPromise = null

const PORTAL_VOICE_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}
const PORTAL_VOICE_VOLUME_STORAGE_KEY = 'aumhum.portalVoice.outputBoostPercent'
const PORTAL_VOICE_DEFAULT_VOLUME_BOOST_PERCENT = 100
const PORTAL_VOICE_MIN_VOLUME_BOOST_PERCENT = 100
const PORTAL_VOICE_MAX_VOLUME_BOOST_PERCENT = 250

function clampPortalVoiceVolumeBoostPercent(value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return PORTAL_VOICE_DEFAULT_VOLUME_BOOST_PERCENT
  }

  return Math.min(
    PORTAL_VOICE_MAX_VOLUME_BOOST_PERCENT,
    Math.max(PORTAL_VOICE_MIN_VOLUME_BOOST_PERCENT, Math.round(numericValue)),
  )
}

function loadStoredPortalVoiceVolumeBoostPercent() {
  if (typeof window === 'undefined') {
    return PORTAL_VOICE_DEFAULT_VOLUME_BOOST_PERCENT
  }

  try {
    return clampPortalVoiceVolumeBoostPercent(
      window.localStorage?.getItem(PORTAL_VOICE_VOLUME_STORAGE_KEY),
    )
  } catch (error) {
    return PORTAL_VOICE_DEFAULT_VOLUME_BOOST_PERCENT
  }
}

function persistPortalVoiceVolumeBoostPercent(value) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage?.setItem(PORTAL_VOICE_VOLUME_STORAGE_KEY, String(value))
  } catch (error) {
    console.warn('Unable to persist the portal voice output boost preference:', error)
  }
}

let currentPortalVoiceVolumeBoostPercent = loadStoredPortalVoiceVolumeBoostPercent()

function detachEmitterListener(emitter, eventName, handler) {
  if (!emitter || !eventName || typeof handler !== 'function') {
    return
  }

  if (typeof emitter.off === 'function') {
    emitter.off(eventName, handler)
    return
  }

  if (typeof emitter.removeListener === 'function') {
    emitter.removeListener(eventName, handler)
    return
  }

  if (typeof emitter.removeEventListener === 'function') {
    emitter.removeEventListener(eventName, handler)
  }
}

async function getPlivoBrowserSdkConstructor() {
  if (!plivoConstructorPromise) {
    plivoConstructorPromise = import('plivo-browser-sdk').then(
      (module) => module?.default || module,
    )
  }

  return plivoConstructorPromise
}

function getNormalizedPortalVoiceErrorText(error) {
  return `${error?.message || ''} ${error?.name || ''}`.trim().toLowerCase()
}

function stopMediaStreamTracks(stream) {
  if (!stream || typeof stream.getTracks !== 'function') {
    return
  }

  stream.getTracks().forEach((track) => {
    try {
      track.stop()
    } catch (error) {
      console.warn('Unable to stop media track during portal voice setup:', error)
    }
  })
}

function getPortalVoiceAudioBoostMultiplier() {
  return Math.max(1, currentPortalVoiceVolumeBoostPercent / 100)
}

function isPortalVoiceBoostableMediaElement(element) {
  if (typeof window === 'undefined' || typeof window.HTMLMediaElement === 'undefined' || !(element instanceof window.HTMLMediaElement)) {
    return false
  }

  const audioTrackCount = element?.srcObject?.getAudioTracks?.()?.length || 0
  return audioTrackCount > 0 || Boolean(element.currentSrc || element.src)
}

function setPortalVoiceElementAudible(element) {
  if (!element) {
    return
  }

  try {
    element.muted = false
  } catch (error) {
    console.warn('Unable to unmute the portal voice media element:', error)
  }

  try {
    element.volume = 1
  } catch (error) {
    console.warn('Unable to set the portal voice media element volume:', error)
  }
}

async function ensurePortalVoiceAudioContext() {
  if (typeof window === 'undefined') {
    return null
  }

  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
  if (!AudioContextConstructor) {
    return null
  }

  if (!portalVoiceAudioContext) {
    try {
      portalVoiceAudioContext = new AudioContextConstructor()
    } catch (error) {
      console.warn('Unable to initialize the portal voice audio context:', error)
      return null
    }
  }

  if (portalVoiceAudioContext.state === 'suspended') {
    try {
      await portalVoiceAudioContext.resume()
    } catch (error) {
      console.warn('Unable to resume the portal voice audio context:', error)
    }
  }

  return portalVoiceAudioContext
}

function updatePortalVoiceGainNode(gainNode) {
  if (!gainNode?.gain) {
    return
  }

  if (!isPortalVoiceCallLive(currentState.status)) {
    gainNode.gain.value = 1
    return
  }

  gainNode.gain.value = currentState?.holdActive ? 0 : getPortalVoiceAudioBoostMultiplier()
}

function disconnectBoostedPortalVoiceMediaElement(element) {
  const entry = boostedPortalVoiceMediaElements.get(element)
  if (!entry) {
    return
  }

  try {
    entry.sourceNode?.disconnect?.()
  } catch (error) {
    console.warn('Unable to disconnect the portal voice source node:', error)
  }

  try {
    entry.gainNode?.disconnect?.()
  } catch (error) {
    console.warn('Unable to disconnect the portal voice gain node:', error)
  }

  boostedPortalVoiceMediaElements.delete(element)
}

function cleanupDetachedPortalVoiceMediaElements() {
  boostedPortalVoiceMediaElements.forEach((value, element) => {
    if (!element?.isConnected) {
      disconnectBoostedPortalVoiceMediaElement(element)
    }
  })
}

function bindPortalVoiceMediaBoostRetry(element) {
  if (!element || portalVoiceBoostRetryBoundElements.has(element)) {
    return
  }

  portalVoiceBoostRetryBoundElements.add(element)
  const retryBoost = () => {
    void boostPortalVoiceMediaElement(element)
  }

  element.addEventListener('loadedmetadata', retryBoost)
  element.addEventListener('play', retryBoost)
  element.addEventListener('canplay', retryBoost)
}

async function boostPortalVoiceMediaElement(element) {
  if (!isPortalVoiceBoostableMediaElement(element)) {
    return
  }

  setPortalVoiceElementAudible(element)

  const existingEntry = boostedPortalVoiceMediaElements.get(element)
  if (existingEntry) {
    updatePortalVoiceGainNode(existingEntry.gainNode)
    return
  }

  const audioContext = await ensurePortalVoiceAudioContext()
  if (!audioContext) {
    return
  }

  try {
    const sourceNode = audioContext.createMediaElementSource(element)
    const gainNode = audioContext.createGain()
    updatePortalVoiceGainNode(gainNode)
    sourceNode.connect(gainNode)
    gainNode.connect(audioContext.destination)
    boostedPortalVoiceMediaElements.set(element, {
      sourceNode,
      gainNode,
    })
  } catch (error) {
    const message = String(error?.message || '').toLowerCase()
    if (!message.includes('already connected')) {
      console.warn('Unable to apply the portal voice audio boost:', error)
    }
  }
}

function boostPortalVoiceMediaElementsFromRoot(root = null) {
  if (typeof document === 'undefined') {
    return
  }

  const effectiveRoot = root || document
  cleanupDetachedPortalVoiceMediaElements()

  const mediaElements = []
  if (typeof window !== 'undefined' && typeof window.HTMLMediaElement !== 'undefined' && effectiveRoot instanceof window.HTMLMediaElement) {
    mediaElements.push(effectiveRoot)
  }
  if (typeof effectiveRoot?.querySelectorAll === 'function') {
    effectiveRoot.querySelectorAll('audio,video').forEach((element) => {
      mediaElements.push(element)
    })
  }

  mediaElements.forEach((element) => {
    bindPortalVoiceMediaBoostRetry(element)
    void boostPortalVoiceMediaElement(element)
  })
}

function ensurePortalVoiceAudioBoostObserver() {
  if (portalVoiceAudioBoostObserver || typeof MutationObserver === 'undefined') {
    return
  }
  if (typeof document === 'undefined' || !document.body) {
    return
  }

  portalVoiceAudioBoostObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!node || node.nodeType !== 1) {
          return
        }
        boostPortalVoiceMediaElementsFromRoot(node)
      })
    })
  })

  portalVoiceAudioBoostObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })
}

function stopPortalVoiceAudioBoostObserver() {
  if (!portalVoiceAudioBoostObserver) {
    return
  }

  portalVoiceAudioBoostObserver.disconnect()
  portalVoiceAudioBoostObserver = null
}

function syncPortalVoiceAudioBoost() {
  cleanupDetachedPortalVoiceMediaElements()

  boostedPortalVoiceMediaElements.forEach(({ gainNode }) => {
    updatePortalVoiceGainNode(gainNode)
  })

  if (!isPortalVoiceCallLive(currentState.status)) {
    stopPortalVoiceAudioBoostObserver()
    return
  }

  ensurePortalVoiceAudioBoostObserver()
  boostPortalVoiceMediaElementsFromRoot(typeof document === 'undefined' ? null : document)
}

async function primePlivoMicrophoneAccess() {
  if (!navigator?.mediaDevices?.getUserMedia) {
    return {
      preferredInputDeviceId: '',
    }
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: PORTAL_VOICE_AUDIO_CONSTRAINTS,
    video: false,
  })

  try {
    const preferredInputDeviceId =
      stream?.getAudioTracks?.()?.[0]?.getSettings?.()?.deviceId || ''

    return {
      preferredInputDeviceId,
    }
  } finally {
    stopMediaStreamTracks(stream)
  }
}

async function configurePlivoAudioDevices(client, audioPrimer = {}) {
  if (!client?.audio) {
    return
  }

  try {
    await client.audio.revealAudioDevices?.('audio')
  } catch (error) {
    console.warn('Unable to reveal Plivo audio devices:', error)
  }

  let availableInputDevices = []
  try {
    availableInputDevices = (await client.audio.availableDevices?.('input')) || []
    if (!availableInputDevices.length) {
      availableInputDevices = (await client.audio.availableDevices?.('audioinput')) || []
    }
    if (!availableInputDevices.length) {
      availableInputDevices = (await client.audio.availableDevices?.()) || []
    }
  } catch (error) {
    console.warn('Unable to load Plivo microphone devices:', error)
  }

  const preferredInputDeviceId = audioPrimer?.preferredInputDeviceId || ''
  const selectedInputDeviceId =
    availableInputDevices.find((device) => device?.deviceId === preferredInputDeviceId)?.deviceId ||
    availableInputDevices[0]?.deviceId ||
    ''

  if (selectedInputDeviceId) {
    try {
      client.audio?.microphoneDevices?.set?.(selectedInputDeviceId)
    } catch (error) {
      console.warn('Unable to set the Plivo microphone device:', error)
    }
  }
}

function ensurePlivoClientUnmuted(client) {
  if (!client || typeof client.unmute !== 'function') {
    return
  }

  try {
    client.unmute()
  } catch (error) {
    console.warn('Unable to unmute the Plivo call client:', error)
  }
}

function normalizePortalVoiceDeviceLabel(device, fallbackPrefix, index) {
  const rawLabel = typeof device?.label === 'string' ? device.label.trim() : ''
  if (rawLabel) {
    return rawLabel
  }

  const rawDeviceId = typeof device?.deviceId === 'string' ? device.deviceId : ''
  const suffix = rawDeviceId ? rawDeviceId.slice(-6) : '${index + 1}'
  return '${fallbackPrefix} ${index + 1} • ${suffix}'
}

function buildPortalVoiceDeviceOption(device, fallbackPrefix, index) {
  const deviceId = typeof device?.deviceId === 'string' ? device.deviceId : ''
  return {
    deviceId,
    label: normalizePortalVoiceDeviceLabel(device, fallbackPrefix, index),
    kind: device?.kind || '',
  }
}

function withPortalVoiceDefaultOutputDevice(outputDevices = []) {
  if (outputDevices.some((device) => device?.deviceId === 'default')) {
    return outputDevices
  }

  return [
    {
      deviceId: 'default',
      label: 'System default speaker',
      kind: 'audiooutput',
    },
    ...outputDevices,
  ]
}

async function listPortalVoiceBrowserDevices() {
  if (!navigator?.mediaDevices?.enumerateDevices) {
    return {
      inputDevices: [],
      outputDevices: withPortalVoiceDefaultOutputDevice([]),
    }
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  const inputDevices = devices
    .filter((device) => device?.kind === 'audioinput')
    .map((device, index) => buildPortalVoiceDeviceOption(device, 'Microphone', index))
  const outputDevices = withPortalVoiceDefaultOutputDevice(
    devices
      .filter((device) => device?.kind === 'audiooutput')
      .map((device, index) => buildPortalVoiceDeviceOption(device, 'Speaker', index)),
  )

  return {
    inputDevices,
    outputDevices,
  }
}

function extractPlivoErrorMessage(error, fallbackMessage) {
  if (!error) {
    return fallbackMessage
  }

  if (typeof error === 'string') {
    return error
  }

  if (typeof error?.reason === 'string' && error.reason.trim()) {
    return error.reason
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message
  }

  if (typeof error?.code === 'string' || typeof error?.code === 'number') {
    return `${fallbackMessage} (${error.code})`
  }

  return fallbackMessage
}

export function getPortalVoiceFriendlyErrorMessage(
  error,
  fallbackMessage = 'Unable to place the portal voice call.',
) {
  const normalizedText = getNormalizedPortalVoiceErrorText(error)

  if (
    normalizedText.includes('notfounderror') ||
    normalizedText.includes('not found') ||
    normalizedText.includes('no audio input') ||
    normalizedText.includes('no microphone')
  ) {
    return 'No microphone was found for this phone call. Connect a microphone and try again.'
  }

  if (
    normalizedText.includes('notallowederror') ||
    normalizedText.includes('permission denied') ||
    normalizedText.includes('permission dismissed') ||
    normalizedText.includes('microphone permission') ||
    normalizedText.includes('permission')
  ) {
    return 'Microphone access is required to place this phone call. Allow microphone access, then try again.'
  }

  if (
    normalizedText.includes('microphone') ||
    normalizedText.includes('audio device') ||
    normalizedText.includes('device in use') ||
    normalizedText.includes('notreadableerror') ||
    normalizedText.includes('could not start audio source') ||
    normalizedText.includes('trackstarterror') ||
    normalizedText.includes('getusermedia')
  ) {
    return 'Your microphone is currently being used by another call or app. End that call or close the other app, then try again.'
  }

  return error?.message || fallbackMessage
}

function buildIdleState() {
  return {
    status: 'idle',
    actorRole: 'mentor',
    providerType: null,
    interactionId: null,
    bookedEventId: null,
    clinicBrandId: null,
    clinicId: null,
    practitionerId: null,
    patientId: null,
    targetRole: null,
    eventType: null,
    eventTypesCsv: null,
    uiSurface: null,
    uiInstanceKey: null,
    destinationType: null,
    destinationLabel: '',
    maskedDestination: '',
    outboundCallerNumber: '',
    outboundCallerNumberMasked: '',
    preferredProviderType: null,
    providerFallbackUsed: false,
    providerBootstrapFallbackReason: '',
    recordingEnabled: false,
    muted: false,
    holdActive: false,
    holdAppliedMute: false,
    availableInputDevices: [],
    availableOutputDevices: [],
    selectedInputDeviceId: '',
    selectedOutputDeviceId: 'default',
    audioDeviceError: '',
    outputDeviceSelectionSupported: false,
    renewalEnabled: false,
    renewalAllowed: false,
    renewalInFlight: false,
    renewalToken: null,
    renewalCount: 0,
    callTimeLimitSeconds: 0,
    renewalIncrementSeconds: 0,
    maxCallDurationSeconds: 0,
    callExpiresAtMs: null,
    remainingSeconds: 0,
    connectedAtMs: null,
    endedAtMs: null,
    elapsedSeconds: 0,
    endedReason: '',
    error: '',
    volumeBoostPercent: currentPortalVoiceVolumeBoostPercent,
  }
}

let currentState = buildIdleState()
let currentDevice = null
let currentConnection = null
let currentPlivoClient = null
let currentProviderType = null
let disconnectRequestedByUser = false
let countdownTimerId = null
let portalVoiceAudioContext = null
let portalVoiceAudioBoostObserver = null
const boostedPortalVoiceMediaElements = new Map()
const portalVoiceBoostRetryBoundElements = new WeakSet()

function isPortalVoiceOutputSelectionSupported() {
  if (currentProviderType === PROVIDER_TWILIO) {
    return !!currentDevice?.audio?.isOutputSelectionSupported
  }

  return typeof window !== 'undefined' && typeof window.HTMLMediaElement !== 'undefined' && typeof window.HTMLMediaElement.prototype?.setSinkId === 'function'
}

function getCurrentPortalVoiceInputDeviceId() {
  if (currentProviderType === PROVIDER_PLIVO) {
    return currentPlivoClient?.audio?.microphoneDevices?.get?.() || ''
  }

  return currentDevice?.audio?.inputDevice?.deviceId || ''
}

function getCurrentPortalVoiceOutputDeviceId() {
  if (currentProviderType === PROVIDER_PLIVO) {
    return currentPlivoClient?.audio?.speakerDevices?.get?.() || 'default'
  }

  const twilioOutputDevices = currentDevice?.audio?.speakerDevices?.get?.()
  if (twilioOutputDevices instanceof Set) {
    return Array.from(twilioOutputDevices)[0]?.deviceId || 'default'
  }

  return 'default'
}

function getPortalVoiceProviderMuteState() {
  if (currentProviderType === PROVIDER_PLIVO) {
    return !!currentPlivoClient?.isCallMuted
  }

  if (currentConnection && typeof currentConnection.isMuted === 'function') {
    return !!currentConnection.isMuted()
  }

  return !!currentState?.muted
}

function setPortalVoiceProviderMuteState(shouldMute) {
  if (currentProviderType === PROVIDER_PLIVO) {
    if (!currentPlivoClient) {
      return
    }

    if (shouldMute && typeof currentPlivoClient.mute === 'function') {
      currentPlivoClient.mute()
      return
    }

    if (!shouldMute && typeof currentPlivoClient.unmute === 'function') {
      currentPlivoClient.unmute()
    }
    return
  }

  if (currentConnection && typeof currentConnection.mute === 'function') {
    currentConnection.mute(shouldMute)
  }
}

async function refreshPortalVoiceAudioDevicesInternal({ suppressErrors = false } = {}) {
  try {
    if (currentProviderType === PROVIDER_PLIVO) {
      try {
        await currentPlivoClient?.audio?.revealAudioDevices?.('audio')
      } catch (error) {
        console.warn('Unable to reveal Plivo audio devices for refresh:', error)
      }
    }

    const { inputDevices, outputDevices } = await listPortalVoiceBrowserDevices()
    setState({
      ...currentState,
      availableInputDevices: inputDevices,
      availableOutputDevices: outputDevices,
      selectedInputDeviceId: getCurrentPortalVoiceInputDeviceId() || inputDevices[0]?.deviceId || '',
      selectedOutputDeviceId: getCurrentPortalVoiceOutputDeviceId() || outputDevices[0]?.deviceId || 'default',
      audioDeviceError: '',
      outputDeviceSelectionSupported: isPortalVoiceOutputSelectionSupported(),
    })

    return getPortalVoiceCallState()
  } catch (error) {
    console.warn('Unable to refresh portal voice audio devices:', error)
    const message = 'Unable to load the available microphone and speaker devices right now.'
    setState({
      ...currentState,
      audioDeviceError: message,
      outputDeviceSelectionSupported: isPortalVoiceOutputSelectionSupported(),
    })
    if (!suppressErrors) {
      toast.error(message)
    }
    throw error
  }
}

function parseServerInstant(value) {
  if (!value) {
    return null
  }

  const directTimestamp = Date.parse(value)
  if (!Number.isNaN(directTimestamp)) {
    return directTimestamp
  }

  const utcTimestamp = Date.parse(`${value} UTC`)
  return Number.isNaN(utcTimestamp) ? null : utcTimestamp
}

function getRemainingSeconds(callExpiresAtMs) {
  if (!callExpiresAtMs) {
    return 0
  }
  return Math.max(0, Math.ceil((callExpiresAtMs - Date.now()) / 1000))
}

function getElapsedSecondsForState(state) {
  if (!state?.connectedAtMs) {
    return state?.elapsedSeconds || 0
  }

  const effectiveEndTime = state?.endedAtMs || Date.now()
  return Math.max(0, Math.floor((effectiveEndTime - state.connectedAtMs) / 1000))
}

function isPortalVoiceCallLive(status) {
  return [
    'connecting',
    'ringing',
    'connected',
    'reconnecting',
    'on-hold',
    'ending',
  ].includes(status)
}

function isPortalVoiceCallSettled(status) {
  return status === 'ended'
}

function syncCountdownTimer() {
  if (countdownTimerId) {
    clearInterval(countdownTimerId)
    countdownTimerId = null
  }

  if (currentState.status === 'idle' || isPortalVoiceCallSettled(currentState.status)) {
    return
  }

  countdownTimerId = window.setInterval(() => {
    if (currentState.status === 'idle') {
      syncCountdownTimer()
      return
    }

    const remainingSeconds = getRemainingSeconds(currentState.callExpiresAtMs)
    const elapsedSeconds = getElapsedSecondsForState(currentState)
    if (
      remainingSeconds !== currentState.remainingSeconds ||
      elapsedSeconds !== currentState.elapsedSeconds
    ) {
      currentState = {
        ...currentState,
        remainingSeconds,
        elapsedSeconds,
      }
      emitState()
    }
  }, 1000)
}

function emitState() {
  const snapshot = { ...currentState }
  listeners.forEach((listener) => {
    try {
      listener(snapshot)
    } catch (error) {
      console.error('Portal voice state listener failed:', error)
    }
  })
}

function setState(nextState) {
  const nextVolumeBoostPercent = clampPortalVoiceVolumeBoostPercent(
    nextState?.volumeBoostPercent ?? currentPortalVoiceVolumeBoostPercent,
  )

  currentPortalVoiceVolumeBoostPercent = nextVolumeBoostPercent
  currentState = {
    ...nextState,
    volumeBoostPercent: nextVolumeBoostPercent,
    remainingSeconds: getRemainingSeconds(nextState?.callExpiresAtMs),
    elapsedSeconds: getElapsedSecondsForState(nextState),
  }
  syncCountdownTimer()
  syncPortalVoiceAudioBoost()
  emitState()
}

function resetState() {
  disconnectRequestedByUser = false
  setState(buildIdleState())
}

function stopLocalTracksFromPeerConnection(peerConnection) {
  if (!peerConnection) {
    return
  }

  try {
    if (typeof peerConnection.getSenders === 'function') {
      peerConnection.getSenders().forEach((sender) => {
        const track = sender?.track
        if (track?.kind === 'audio') {
          try {
            track.stop()
          } catch (error) {
            console.warn('Unable to stop portal voice sender track:', error)
          }
        }
      })
    }

    if (typeof peerConnection.getLocalStreams === 'function') {
      peerConnection.getLocalStreams().forEach((stream) => {
        stopMediaStreamTracks(stream)
      })
    }
  } catch (error) {
    console.warn('Unable to stop portal voice peer connection tracks:', error)
  }
}

function releaseTwilioAudioResources(connection) {
  if (!connection) {
    return
  }

  try {
    stopMediaStreamTracks(connection.getLocalStream?.())
  } catch (error) {
    console.warn('Unable to stop Twilio local stream:', error)
  }

  try {
    stopLocalTracksFromPeerConnection(connection?._mediaHandler?.version?.pc)
  } catch (error) {
    console.warn('Unable to stop Twilio peer connection tracks:', error)
  }
}

function releasePlivoAudioResources(client) {
  if (!client) {
    return
  }

  try {
    const peerConnectionResult = client.getPeerConnection?.()
    stopLocalTracksFromPeerConnection(peerConnectionResult?.pc)
  } catch (error) {
    console.warn('Unable to stop Plivo peer connection tracks:', error)
  }
}

function destroyTwilioClient({ skipDisconnect = false } = {}) {
  const activeConnection = currentConnection
  currentConnection = null

  if (activeConnection && !skipDisconnect) {
    try {
      activeConnection.disconnect()
    } catch (error) {
      console.warn('Error disconnecting active portal voice call:', error)
    }
  }

  const activeDevice = currentDevice
  currentDevice = null

  releaseTwilioAudioResources(activeConnection)

  if (activeDevice) {
    try {
      activeDevice.destroy()
    } catch (error) {
      console.warn('Error destroying Twilio device:', error)
    }
  }
}

function destroyPlivoClient({ skipHangup = false } = {}) {
  const activeClient = currentPlivoClient
  currentPlivoClient = null

  if (!activeClient) {
    return
  }

  if (!skipHangup && typeof activeClient.hangup === 'function') {
    try {
      activeClient.hangup()
    } catch (error) {
      console.warn('Error hanging up Plivo call:', error)
    }
  }

  if (typeof activeClient.logout === 'function') {
    try {
      activeClient.logout()
    } catch (error) {
      console.warn('Error logging out Plivo client:', error)
    }
  }

  releasePlivoAudioResources(activeClient)
}

function destroyProviderClient(options = {}) {
  const providerType = currentProviderType
  currentProviderType = null

  if (providerType === PROVIDER_PLIVO) {
    destroyPlivoClient(options)
    return
  }

  destroyTwilioClient(options)
}

function markCallConnected(route) {
  const now = Date.now()
  const expiresAtMs =
    currentState.callExpiresAtMs ||
    (route?.callTimeLimitSeconds ? now + route.callTimeLimitSeconds * 1000 : null)
  const wasConnected = currentState.status === 'connected'

  setState({
    ...currentState,
    status: currentState?.holdActive ? 'on-hold' : 'connected',
    destinationLabel: route?.destinationLabel || route?.title || currentState.destinationLabel,
    maskedDestination: route?.maskedDestination || currentState.maskedDestination,
    callExpiresAtMs: expiresAtMs,
    connectedAtMs: currentState.connectedAtMs || now,
    endedAtMs: null,
    endedReason: '',
    muted: getPortalVoiceProviderMuteState(),
    error: '',
  })

  if (!wasConnected) {
    toast.success(
      `Connected to ${route?.destinationLabel || route?.title || 'the participant'}${
        route?.maskedDestination ? ` (${route.maskedDestination})` : ''
      }.`,
    )
  }
}

function transitionPortalVoiceCallToEnded({ reason = '', error = '' } = {}) {
  const settledState = {
    ...currentState,
    status: 'ended',
    endedAtMs: Date.now(),
    endedReason: reason || currentState.endedReason || '',
    error: error || '',
    remainingSeconds: 0,
    renewalInFlight: false,
    renewalAllowed: false,
  }
  setState(settledState)
}

function handleProviderDisconnect(skipCleanupOptions) {
  if (disconnectRequestedByUser) {
    toast.info('The portal voice call ended.')
  }
  destroyProviderClient(skipCleanupOptions)
  transitionPortalVoiceCallToEnded({
    reason: disconnectRequestedByUser ? 'You ended the phone call.' : 'The phone call ended.',
  })
}

function handleProviderError(error, fallbackMessage, skipCleanupOptions) {
  console.error('Portal voice provider error:', error)
  const friendlyMessage = getPortalVoiceFriendlyErrorMessage(error, fallbackMessage)
  toast.error(friendlyMessage)
  destroyProviderClient(skipCleanupOptions)
  transitionPortalVoiceCallToEnded({
    reason: 'The phone call ended with an error.',
    error: friendlyMessage,
  })
}

function attachTwilioDeviceListeners(device) {
  device.on('error', (error) => {
    handleProviderError(error, 'The portal voice call failed to initialize.', {
      skipDisconnect: true,
    })
  })
}

function attachTwilioConnectionListeners(connection, route) {
  connection.on('ringing', () => {
    setState({
      ...currentState,
      status: 'ringing',
      destinationLabel: route?.destinationLabel || route?.title || currentState.destinationLabel,
      maskedDestination: route?.maskedDestination || currentState.maskedDestination,
      error: '',
    })
  })

  connection.on('accept', () => {
    markCallConnected(route)
  })

  connection.on('cancel', () => {
    toast.info('The portal voice call was cancelled.')
    destroyProviderClient({ skipDisconnect: true })
    transitionPortalVoiceCallToEnded({
      reason: 'The phone call was cancelled before it connected.',
    })
  })

  connection.on('reject', () => {
    toast.info('The portal voice call was rejected.')
    destroyProviderClient({ skipDisconnect: true })
    transitionPortalVoiceCallToEnded({
      reason: 'The call was rejected by the recipient.',
    })
  })

  connection.on('disconnect', () => {
    handleProviderDisconnect({ skipDisconnect: true })
  })

  connection.on?.('mute', (isMuted) => {
    setState({
      ...currentState,
      muted: !!isMuted,
      error: '',
    })
  })

  connection.on('error', (error) => {
    handleProviderError(error, 'The portal voice call ended with an error.', {
      skipDisconnect: true,
    })
  })

  connection.on?.('reconnecting', () => {
    setState({
      ...currentState,
      status: 'reconnecting',
      error: '',
    })
  })

  connection.on?.('reconnected', () => {
    markCallConnected(route)
  })
}

function attachPlivoClientListeners(client, route) {
  client.on('onCalling', () => {
    ensurePlivoClientUnmuted(client)
    setState({
      ...currentState,
      status: 'connecting',
      destinationLabel: route?.destinationLabel || route?.title || currentState.destinationLabel,
      maskedDestination: route?.maskedDestination || currentState.maskedDestination,
      error: '',
    })
  })

  client.on('onCallRemoteRinging', () => {
    setState({
      ...currentState,
      status: 'ringing',
      destinationLabel: route?.destinationLabel || route?.title || currentState.destinationLabel,
      maskedDestination: route?.maskedDestination || currentState.maskedDestination,
      error: '',
    })
  })

  client.on('onCallAnswered', () => {
    ensurePlivoClientUnmuted(client)
    markCallConnected(route)
  })

  client.on('onCallConnected', () => {
    ensurePlivoClientUnmuted(client)
    markCallConnected(route)
  })

  client.on('onMediaPermission', (payload) => {
    if (payload?.status && payload.status !== 'failure') {
      ensurePlivoClientUnmuted(client)
      return
    }

    handleProviderError(
      new Error(
        extractPlivoErrorMessage(payload, 'Microphone permission is required to place this call.'),
      ),
      'Microphone permission is required to place this call.',
      {
        skipHangup: true,
      },
    )
  })

  client.on('onGetUserMediaFailed', (payload) => {
    handleProviderError(
      new Error(
        extractPlivoErrorMessage(payload, 'Microphone access is required to place this call.'),
      ),
      'Microphone access is required to place this call.',
      {
        skipHangup: true,
      },
    )
  })

  client.on('onCallFailed', (payload) => {
    handleProviderError(
      new Error(extractPlivoErrorMessage(payload, 'The portal voice call ended with an error.')),
      'The portal voice call ended with an error.',
      {
        skipHangup: true,
      },
    )
  })

  client.on('onCallTerminated', () => {
    handleProviderDisconnect({ skipHangup: true })
  })
}

function loginPlivoClient(client, username, password) {
  return new Promise((resolve, reject) => {
    let settled = false
    let timeoutId = null

    function cleanup() {
      clearTimeout(timeoutId)
      detachEmitterListener(client, 'onLogin', handleLogin)
      detachEmitterListener(client, 'onLoginFailed', handleLoginFailed)
    }

    function handleLogin() {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      resolve(true)
    }

    function handleLoginFailed(payload) {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      reject(
          new Error(extractPlivoErrorMessage(payload, 'Unable to initialize the Plivo softphone.')),
      )
    }

    timeoutId = window.setTimeout(() => {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      reject(new Error('Timed out while initializing the Plivo softphone.'))
    }, 20000)

    client.on('onLogin', handleLogin)
    client.on('onLoginFailed', handleLoginFailed)

    const loginAccepted = client.login(username, password)
    if (loginAccepted === false) {
      settled = true
      cleanup()
      reject(new Error('Unable to initialize the Plivo softphone.'))
    }
  })
}

async function startTwilioProviderCall(bootstrap, route) {
  currentProviderType = PROVIDER_TWILIO
  currentDevice = new Device(bootstrap.accessToken, {
    logLevel: 1,
    codecPreferences: ['opus', 'pcmu'],
    closeProtection: false,
  })
  attachTwilioDeviceListeners(currentDevice)

  const connectionParams = {}
  if (bootstrap?.requestToken) {
    connectionParams.requestToken = bootstrap.requestToken
  }
  const providerTransportToken =
    bootstrap?.providerTransportToken || bootstrap?.providerSession?.providerTransportToken || ''
  if (providerTransportToken) {
    connectionParams.providerTransportToken = providerTransportToken
  }

  currentConnection = await currentDevice.connect({
    params: connectionParams,
  })
  attachTwilioConnectionListeners(currentConnection, route)
}

async function startPlivoProviderCall(bootstrap, route, audioPrimer) {
  const providerSession = bootstrap?.providerSession || {}
  const endpointUsername = providerSession?.endpointUsername || bootstrap?.identity
  const endpointPassword = providerSession?.endpointPassword
  const callTarget = providerSession?.callTarget
  const providerTransportToken =
    bootstrap?.providerTransportToken ||
    providerSession?.providerTransportToken ||
    providerSession?.callHeaders?.[PLIVO_TRANSPORT_TOKEN_HEADER] ||
    ''
  const callHeaders = {
    ...(providerSession?.callHeaders || {}),
  }
  delete callHeaders['X-PH-RequestToken']
  if (providerTransportToken) {
    callHeaders[PLIVO_TRANSPORT_TOKEN_HEADER] = providerTransportToken
  }

  if (!endpointUsername || !endpointPassword || !callTarget || !providerTransportToken) {
    throw new Error('Plivo softphone session is incomplete.')
  }

  const PlivoBrowserSdk = await getPlivoBrowserSdkConstructor()
  const plivoInstance = new PlivoBrowserSdk({
    debug: 'WARN',
    permOnClick: true,
    closeProtection: false,
    enableTracking: false,
    enableQualityTracking: 'NONE',
    preDetectOwa: true,
    useDefaultAudioDevice: true,
    audioConstraints: PORTAL_VOICE_AUDIO_CONSTRAINTS,
  })
  const client = plivoInstance?.client || plivoInstance

  if (!client || typeof client.login !== 'function' || typeof client.call !== 'function') {
    throw new Error('Unable to initialize the Plivo softphone.')
  }

  currentProviderType = PROVIDER_PLIVO
  currentPlivoClient = client
  attachPlivoClientListeners(client, route)

  await loginPlivoClient(client, endpointUsername, endpointPassword)
  await configurePlivoAudioDevices(client, audioPrimer)

  const callAccepted = client.call(callTarget, callHeaders)
  if (callAccepted === false) {
    throw new Error('Unable to place the Plivo portal voice call.')
  }

  ensurePlivoClientUnmuted(client)
}

export function getPortalVoiceCallState() {
  return { ...currentState }
}

export function subscribePortalVoiceCallState(listener) {
  if (typeof listener !== 'function') {
    return () => {}
  }

  listeners.add(listener)
  listener(getPortalVoiceCallState())
  return () => {
    listeners.delete(listener)
  }
}

export function setPortalVoiceCallVolumeBoostPercent(value) {
  const nextVolumeBoostPercent = clampPortalVoiceVolumeBoostPercent(value)
  currentPortalVoiceVolumeBoostPercent = nextVolumeBoostPercent
  persistPortalVoiceVolumeBoostPercent(nextVolumeBoostPercent)
  setState({
    ...getPortalVoiceCallState(),
    volumeBoostPercent: nextVolumeBoostPercent,
  })
}

export async function startPortalVoiceCall({
  actorRole = 'mentor',
  bookedEventId,
  clinicBrandId,
  clinicId,
  practitionerId,
  patientId,
  targetRole,
  eventType,
  eventTypesCsv,
  destinationType,
  uiSurface,
  uiInstanceKey,
  providerTypeHint,
}) {
  if (isPortalVoiceCallLive(currentState.status)) {
    throw new Error('A portal voice call is already in progress.')
  }
  if (isPortalVoiceCallSettled(currentState.status)) {
    resetState()
  }

  let plivoAudioPrimer = null
  if (providerTypeHint === PROVIDER_PLIVO) {
    plivoAudioPrimer = await primePlivoMicrophoneAccess()
  }

  const bootstrap = await createPortalVoiceBootstrap({
    actorRole,
    bookedEventId,
    clinicBrandId,
    clinicId,
    practitionerId,
    patientId,
    targetRole,
    eventType,
    eventTypesCsv,
    destinationType,
    uiSurface,
  })

  if (!bootstrap?.ok) {
    throw new Error(bootstrap?.message || 'Unable to prepare the portal voice call.')
  }

  const route = bootstrap?.route || {}
  const providerType =
    bootstrap?.providerType || route?.providerType || bootstrap?.providerSession?.providerType || PROVIDER_TWILIO

  if (providerType === PROVIDER_PLIVO && !plivoAudioPrimer) {
    plivoAudioPrimer = await primePlivoMicrophoneAccess()
  }

  setState({
    status: 'connecting',
    actorRole,
    providerType,
    interactionId: bootstrap?.callSessionId || null,
    bookedEventId,
    clinicBrandId: clinicBrandId || null,
    clinicId: clinicId || null,
    practitionerId: practitionerId || null,
    patientId: patientId || null,
    targetRole: targetRole || null,
    eventType: eventType || null,
    eventTypesCsv: eventTypesCsv || null,
    uiSurface: uiSurface || null,
    uiInstanceKey: uiInstanceKey || null,
    destinationType,
    destinationLabel: route?.destinationLabel || route?.title || 'Portal Call',
    maskedDestination: route?.maskedDestination || '',
    outboundCallerNumber: route?.outboundCallerNumber || '',
    outboundCallerNumberMasked: route?.outboundCallerNumberMasked || '',
    preferredProviderType: providerTypeHint || route?.preferredProviderType || route?.providerType || null,
    providerFallbackUsed: !!route?.fallbackUsed,
    providerBootstrapFallbackReason: route?.providerBootstrapFallbackReason || '',
    recordingEnabled: !!route?.recordingEnabled,
    muted: false,
    holdActive: false,
    holdAppliedMute: false,
    availableInputDevices: [],
    availableOutputDevices: withPortalVoiceDefaultOutputDevice([]),
    selectedInputDeviceId: '',
    selectedOutputDeviceId: 'default',
    audioDeviceError: '',
    outputDeviceSelectionSupported: false,
    renewalEnabled: !!route?.renewalEnabled,
    renewalAllowed: !!route?.renewalEnabled,
    renewalInFlight: false,
    renewalToken: bootstrap?.renewalToken || null,
    renewalCount: 0,
    callTimeLimitSeconds: route?.callTimeLimitSeconds || 0,
    renewalIncrementSeconds: route?.renewalIncrementSeconds || 0,
    maxCallDurationSeconds: route?.maxCallDurationSeconds || 0,
    callExpiresAtMs: null,
    connectedAtMs: null,
    endedAtMs: null,
    elapsedSeconds: 0,
    endedReason: '',
    error: '',
  })

  try {
    if (providerType === PROVIDER_PLIVO) {
      await startPlivoProviderCall(bootstrap, route, plivoAudioPrimer)
    } else {
      await startTwilioProviderCall(bootstrap, route)
    }
    void refreshPortalVoiceAudioDevicesInternal({ suppressErrors: true })
  } catch (error) {
    console.error('Error starting portal voice call:', error)
    destroyProviderClient({ skipHangup: true, skipDisconnect: true })
    resetState()
    throw new Error(
      getPortalVoiceFriendlyErrorMessage(error, 'Unable to start the portal voice call.'),
    )
  }

  return {
    ok: true,
    route,
  }
}

export async function extendPortalVoiceCall() {
  if (currentState.status === 'idle' || currentState.status === 'ended') {
    throw new Error('There is no active portal voice call to extend.')
  }
  if (!currentState.renewalAllowed || !currentState.renewalEnabled) {
    throw new Error('This portal voice call cannot be extended.')
  }
  if (!currentState.interactionId || !currentState.renewalToken) {
    throw new Error('A renewal token is not available for this call.')
  }
  if (currentState.renewalInFlight) {
    throw new Error('A call extension is already in progress.')
  }

  const stateSnapshot = getPortalVoiceCallState()
  setState({
    ...stateSnapshot,
    renewalInFlight: true,
    error: '',
  })

  const renewalResult = await renewPortalVoiceCall({
    actorRole: stateSnapshot.actorRole || 'mentor',
    interactionId: stateSnapshot.interactionId,
    renewalToken: stateSnapshot.renewalToken,
  })

  if (!renewalResult?.ok) {
    setState({
      ...getPortalVoiceCallState(),
      renewalInFlight: false,
      error: renewalResult?.message || 'Unable to extend the portal voice call.',
    })
    throw new Error(renewalResult?.message || 'Unable to extend the portal voice call.')
  }

  const expiresAtMs =
    parseServerInstant(renewalResult?.callExpiresAt) ||
    (renewalResult?.remainingSeconds
      ? Date.now() + renewalResult.remainingSeconds * 1000
      : stateSnapshot.callExpiresAtMs)

  setState({
    ...getPortalVoiceCallState(),
    renewalInFlight: false,
    renewalAllowed: !!renewalResult?.renewalAllowed,
    renewalToken: renewalResult?.renewalToken || null,
    renewalCount: renewalResult?.renewalCount ?? stateSnapshot.renewalCount,
    callTimeLimitSeconds: renewalResult?.callTimeLimitSeconds || stateSnapshot.callTimeLimitSeconds,
    renewalIncrementSeconds:
      renewalResult?.renewalIncrementSeconds || stateSnapshot.renewalIncrementSeconds,
    maxCallDurationSeconds:
      renewalResult?.maxCallDurationSeconds || stateSnapshot.maxCallDurationSeconds,
    callExpiresAtMs: expiresAtMs,
    error: '',
  })

  const extensionMinutes = Math.max(
    1,
    Math.round(
      (renewalResult?.renewalIncrementSeconds || stateSnapshot.renewalIncrementSeconds || 0) / 60,
    ),
  )
  toast.success(
    `Extended the portal voice call by ${extensionMinutes} minute${extensionMinutes === 1 ? '' : 's'}.`,
  )

  return {
    ok: true,
    ...renewalResult,
  }
}

export async function refreshPortalVoiceAudioDevices() {
  return refreshPortalVoiceAudioDevicesInternal({ suppressErrors: false })
}

export function togglePortalVoiceMute(forceMuted) {
  if (!isPortalVoiceCallLive(currentState.status)) {
    return getPortalVoiceCallState()
  }

  const shouldMute = typeof forceMuted === 'boolean' ? forceMuted : !getPortalVoiceProviderMuteState()
  setPortalVoiceProviderMuteState(shouldMute)
  setState({
    ...currentState,
    muted: shouldMute,
    error: '',
  })
  return getPortalVoiceCallState()
}

export function togglePortalVoiceHold(forceHeld) {
  if (!['connected', 'on-hold'].includes(currentState.status)) {
    return getPortalVoiceCallState()
  }

  const shouldHold = typeof forceHeld === 'boolean' ? forceHeld : !currentState.holdActive
  if (shouldHold) {
    const providerWasMuted = getPortalVoiceProviderMuteState()
    if (!providerWasMuted) {
      setPortalVoiceProviderMuteState(true)
    }
    setState({
      ...currentState,
      status: 'on-hold',
      holdActive: true,
      holdAppliedMute: !providerWasMuted,
      muted: true,
      error: '',
    })
    toast.info('The call is on hold on this device.')
    return getPortalVoiceCallState()
  }

  if (currentState.holdAppliedMute) {
    setPortalVoiceProviderMuteState(false)
  }

  const muted = currentState.holdAppliedMute ? false : getPortalVoiceProviderMuteState()
  setState({
    ...currentState,
    status: 'connected',
    holdActive: false,
    holdAppliedMute: false,
    muted,
    error: '',
  })
  toast.info('The call is live again.')
  return getPortalVoiceCallState()
}

export async function setPortalVoiceInputDevice(deviceId) {
  if (!deviceId) {
    return getPortalVoiceCallState()
  }

  if (currentProviderType === PROVIDER_PLIVO) {
    currentPlivoClient?.audio?.microphoneDevices?.set?.(deviceId)
  } else {
    await currentDevice?.audio?.setInputDevice?.(deviceId)
  }

  return refreshPortalVoiceAudioDevicesInternal({ suppressErrors: true })
}

export async function setPortalVoiceOutputDevice(deviceId) {
  const normalizedDeviceId = deviceId || 'default'

  if (currentProviderType === PROVIDER_PLIVO) {
    currentPlivoClient?.audio?.speakerDevices?.set?.(normalizedDeviceId)
  } else {
    await currentDevice?.audio?.speakerDevices?.set?.(normalizedDeviceId)
  }

  return refreshPortalVoiceAudioDevicesInternal({ suppressErrors: true })
}

export function endPortalVoiceCall() {
  if (currentState.status === 'idle') {
    return
  }
  if (currentState.status === 'ended') {
    resetState()
    return
  }

  disconnectRequestedByUser = true
  setState({
    ...currentState,
    status: 'ending',
    error: '',
  })

  if (currentProviderType === PROVIDER_PLIVO && currentPlivoClient) {
    if (typeof currentPlivoClient.hangup === 'function') {
      try {
        currentPlivoClient.hangup()
        releasePlivoAudioResources(currentPlivoClient)
        return
      } catch (error) {
        console.warn('Error hanging up Plivo portal voice call:', error)
      }
    }

    destroyProviderClient({ skipHangup: true })
    transitionPortalVoiceCallToEnded({
      reason: 'You ended the phone call.',
    })
    return
  }

  if (currentConnection) {
    try {
      currentConnection.disconnect()
      releaseTwilioAudioResources(currentConnection)
      return
    } catch (error) {
      console.warn('Error disconnecting portal voice connection:', error)
    }
  }

  if (currentDevice) {
    try {
      currentDevice.destroy()
    } catch (error) {
      console.warn('Error destroying Twilio device after portal voice disconnect:', error)
    }
  }

  currentProviderType = null
  transitionPortalVoiceCallToEnded({
    reason: 'You ended the phone call.',
  })
}

export function dismissPortalVoiceCall() {
  resetState()
}
